import type { Zone } from '@seek/audience-zones';
import type { Language } from '@seek/melways-sites';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { TextLink } from 'braid-design-system';

import Subscription from '../common/subscription';
import { getZoneUrls } from '../contactDetails';
import {
  trackUnsubscribeAllFailedPageLoad,
  trackUnsubscribeFailedPageLoad,
} from '../tracking/tealium';

import translations from './.vocab';
import { UnsubscribeIcon } from './svg/unsubscribe-icon';
import type { Action } from './types';

export const UnsubscribeError = ({
  zone,
  language,
  action,
}: {
  language: Language;
  zone: Zone;
  action?: Action;
}) => {
  const { t } = useTranslations(translations);
  const isUnsubscribeAll = action === 'unsubscribe-all';

  if (isUnsubscribeAll) {
    trackUnsubscribeAllFailedPageLoad();
  } else {
    trackUnsubscribeFailedPageLoad();
  }
  metrics.count('unsubscribe-error-page-rendered');

  const zoneUrls = getZoneUrls(zone, language);

  return (
    <Subscription
      title={t('error-subscription-title')}
      cardTitle={t('error-subscription-card-title')}
      body={
        <>
          {t('error-subscription-body', {
            TextLink: (children) => (
              <TextLink
                data-testid="contact-us-link"
                href={zoneUrls.contactUrl}
              >
                {children}
              </TextLink>
            ),
          })}
          <br />
          {t('error-subscription-body-line-2', {
            TextLink: (children) => (
              <TextLink
                data-testid="help-centre-link"
                href={zoneUrls.helpCentreUrl}
              >
                {children}
              </TextLink>
            ),
          })}
        </>
      }
      svg={<UnsubscribeIcon />}
      linkMessage={t('unsubscribe-subscription-link-message')}
    />
  );
};

export default UnsubscribeError;
